import { useRef, useState, useMemo } from 'react'

import { IUserAudio } from 'store/types/audio'
import { IAudioItem } from 'types/audio'

import * as Styled from './styled'
import {
  useCheckCanScroll,
  useSoundsListCallbacks,
  useListHeight
} from './hooks'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { ItemForShadowEffect } from './item-for-shadow-effect'
import { GenreThumbnail } from '../genre-thumbnail'

import { SoundListItem } from './sound-list-item'
import { UploadMusicLoading } from './upload-music-loading'

import { FixedSizeList as List, ListChildComponentProps } from 'react-window'

interface IProps {
  sounds: IUserAudio[]
  uploadingSounds?: IAudioItem[]
  isShowSoundDeleteBtn?: boolean
  paddingTop?: string
  onClickAddButton?: () => void
  activeGenreName?: string
  thumbnail?: string
  isLoadingState?: boolean
}

const ITEM_SIZE = 48
const ITEM_GAP = 8

export const SoundsList: React.FC<IProps> = ({
  sounds,
  uploadingSounds,
  isShowSoundDeleteBtn,
  paddingTop,
  onClickAddButton,
  activeGenreName,
  thumbnail,
  isLoadingState
}) => {
  const [soundsListRef, setSoundsListRef] = useState<HTMLDivElement | null>(
    null
  )

  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const currentPlayingAudioRef = useRef<null | HTMLAudioElement>(null)

  const { listHeight, headerElementsRef } = useListHeight(soundsListRef, [
    uploadingSounds,
    activeGenreName,
    isMobile
  ])

  // Combine uploading sounds and regular sounds
  const allItems = useMemo(() => {
    const uploadingItems =
      uploadingSounds?.map(sound => ({
        type: 'uploading' as const,
        data: sound
      })) || []

    const regularItems = sounds.map(sound => ({
      type: 'regular' as const,
      data: sound
    }))

    return [...uploadingItems, ...regularItems]
  }, [uploadingSounds, sounds])

  // Calculate total content height including uploading sounds
  const totalContentHeight = allItems.length * (ITEM_SIZE + ITEM_GAP)
  const visibleHeight = listHeight

  const { canScrollUp, canScrollDown, onScroll } = useCheckCanScroll(
    soundsListRef,
    totalContentHeight,
    visibleHeight
  )

  const { setDeletableSoundIdCallback, lastDeletedSoundIdRef } =
    useSoundsListCallbacks()

  const Row = useMemo(() => {
    const RowComponent = ({ index, style }: ListChildComponentProps) => {
      const item = allItems[index]

      return (
        <Styled.RowWrapper
          style={{
            ...style
          }}
          key={
            item.type === 'uploading'
              ? `uploading-${item.data.id}`
              : `regular-${item.data.id}`
          }
        >
          {item.type === 'uploading' ? (
            <UploadMusicLoading name={item.data.title} />
          ) : (
            <SoundListItem
              sound={item.data}
              onDeleteIconClick={setDeletableSoundIdCallback}
              isShowSoundDeleteBtn={isShowSoundDeleteBtn || !!item.data.userId}
              isDeletingSound={lastDeletedSoundIdRef === item.data.id}
              currentPlayingAudioRef={currentPlayingAudioRef}
              onClickAddButton={onClickAddButton}
              isLoadingState={isLoadingState}
            />
          )}
        </Styled.RowWrapper>
      )
    }

    RowComponent.displayName = 'SoundsListRow'
    return RowComponent
  }, [
    allItems,
    setDeletableSoundIdCallback,
    lastDeletedSoundIdRef,
    isShowSoundDeleteBtn,
    onClickAddButton,
    isLoadingState
  ])
  // ===========================================================================

  return (
    <Styled.SoundsListContainer>
      <ItemForShadowEffect
        isTop={!!activeGenreName && isMobile}
        isVisible={canScrollUp}
        fromTop
      />

      <Styled.SoundsListWrapper ref={setSoundsListRef} $paddingTop={paddingTop}>
        <div ref={headerElementsRef}>
          {isMobile && activeGenreName && (
            <GenreThumbnail
              thumbnail={thumbnail || ''}
              activeGenreName={activeGenreName || ''}
            />
          )}
        </div>

        <List
          height={listHeight}
          width="100%"
          itemCount={allItems.length}
          itemSize={ITEM_SIZE + ITEM_GAP}
          outerRef={setSoundsListRef}
          onScroll={onScroll}
        >
          {Row}
        </List>
      </Styled.SoundsListWrapper>

      <ItemForShadowEffect isVisible={canScrollDown} />
    </Styled.SoundsListContainer>
  )
}
